import { useEffect, useRef, useState } from 'react';
import { PreflightSummary } from './PreflightSummary';
import { Preflight } from './sendPreparation';

interface ApprovalPreview {
    totalCount: number;
    approvableCount: number;
    confirmationPhrase: string;
}

export interface SendConfirmationConfig {
    campaignId: string;
    campaignName: string;
    subject: string;
    bodyTemplate: string;
    draftMode: 'template' | 'ai';
    sourceDescription: string;
    requiresConsent: boolean;
    limit: number;
    senderProvider: 'spacemail' | 'outlook';
    senderAccountLimit?: number;
    outlookPerSenderDailyLimit?: number;
    idempotencyKey: string;
    audienceOnly?: boolean;
    simpleSend?: boolean;
}

type RequestApi = <T>(path: string, options?: RequestInit) => Promise<T>;

export function SendConfirmationDialog({ config, request, onClose, onReady, onSent }: {
    config: SendConfirmationConfig;
    request: RequestApi;
    onClose: () => void;
    onReady: (preflight: Preflight) => void;
    onSent: (jobId: string, reused: boolean) => void;
}) {
    const dialogRef = useRef<HTMLDialogElement>(null);
    const acting = useRef(false);
    const [preflight, setPreflight] = useState<Preflight | null>(null);
    const [approval, setApproval] = useState<ApprovalPreview | null>(null);
    const [basis, setBasis] = useState<'consent' | 'legitimate_interest_reviewed'>('consent');
    const [evidence, setEvidence] = useState('');
    const [source, setSource] = useState(config.sourceDescription);
    const [reviewed, setReviewed] = useState(false);
    const [audienceConfirmed, setAudienceConfirmed] = useState(false);
    const [busy, setBusy] = useState('load');
    const [error, setError] = useState('');
    const [revision, setRevision] = useState(0);
    const route = `/campaigns/${config.campaignId}`;
    const options = {
        limit: config.limit, senderProvider: config.senderProvider,
        senderAccountLimit: config.senderAccountLimit,
        outlookPerSenderDailyLimit: config.outlookPerSenderDailyLimit,
    };
    const requiresAudienceStep = !config.simpleSend && !audienceConfirmed && Boolean(approval?.approvableCount || config.audienceOnly);
    const reviewComplete = config.simpleSend || (reviewed && source.trim().length >= 3 && (basis !== 'consent' || evidence.trim().length >= 5));
    const canSend = Boolean(preflight?.deliveryMode === 'external' && preflight.eligibleCount > 0 && preflight.capacityShortfall === 0);

    useEffect(() => {
        const dialog = dialogRef.current;
        if (dialog && !dialog.open) dialog.showModal();
        return () => { dialog?.close(); };
    }, []);

    useEffect(() => {
        let cancelled = false;
        setBusy('load'); setError('');
        void (async () => {
            try {
                await request(`${route}/message`, { method: 'PATCH', body: JSON.stringify({
                    subject: config.subject, bodyTemplate: config.bodyTemplate, personalizationMode: config.draftMode,
                }) });
                if (cancelled) return;
                const [previewData, preflightData] = await Promise.all([
                    config.simpleSend ? Promise.resolve({ preview: null })
                        : request<{ preview: ApprovalPreview }>(`${route}/leads/approval-preview`),
                    request<{ preflight: Preflight }>(`${route}/send-preflight`, { method: 'POST', body: JSON.stringify({
                        limit: config.limit, senderProvider: config.senderProvider,
                        senderAccountLimit: config.senderAccountLimit, outlookPerSenderDailyLimit: config.outlookPerSenderDailyLimit,
                    }) }),
                ]);
                if (cancelled) return;
                setApproval(previewData.preview);
                setPreflight(preflightData.preflight);
            } catch (err) {
                if (!cancelled) setError(err instanceof Error ? err.message : String(err));
            } finally {
                if (!cancelled) setBusy('');
            }
        })();
        return () => { cancelled = true; };
    }, [config, request, route, revision]);

    async function confirmAudience() {
        if (acting.current || !reviewComplete || !approval) return;
        acting.current = true; setBusy('review'); setError('');
        try {
            const current = (await request<{ preview: ApprovalPreview }>(`${route}/leads/approval-preview`)).preview;
            if (current.confirmationPhrase !== approval.confirmationPhrase) {
                setApproval(current);
                setError('名单数量有变化，已更新，请重新确认。');
                return;
            }
            if (current.approvableCount > 0) await request(`${route}/leads/approve-bulk`, {
                method: 'POST', body: JSON.stringify({
                    confirmationPhrase: current.confirmationPhrase,
                    attestation: { audienceReviewed: reviewed, senderIdentitiesReviewed: reviewed, basis,
                        sourceDescription: source, consentEvidenceReference: evidence },
                }),
            });
            const result = await request<{ preflight: Preflight }>(`${route}/send-preflight`, { method: 'POST', body: JSON.stringify(options) });
            setPreflight(result.preflight); setAudienceConfirmed(true);
            onReady(result.preflight);
            if (config.audienceOnly) onClose();
        } catch (err) { setError(err instanceof Error ? err.message : String(err)); }
        finally { acting.current = false; setBusy(''); }
    }

    async function send() {
        if (acting.current || !reviewComplete || !canSend || requiresAudienceStep || !preflight) return;
        acting.current = true; setBusy('send'); setError('');
        try {
            const data = await request<{ job: { id: string }; reused: boolean }>(`${route}/send-jobs`, {
                method: 'POST', body: JSON.stringify({
                    ...options, dryRun: false, idempotencyKey: config.idempotencyKey,
                    approvalPhrase: preflight.confirmationPhrase,
                    ...(!config.simpleSend ? { attestation: { audienceReviewed: reviewed, senderIdentitiesReviewed: reviewed, basis,
                        sourceDescription: source, consentEvidenceReference: evidence } } : {}),
                }),
            });
            onSent(data.job.id, data.reused);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
            // Never retry a live send automatically. Keep the same idempotency
            // key, refresh the visible count, and require another explicit click.
            try {
                const result = await request<{ preflight: Preflight }>(`${route}/send-preflight`, { method: 'POST', body: JSON.stringify(options) });
                setPreflight(result.preflight);
            } catch { /* preserve the original send error */ }
        } finally { acting.current = false; setBusy(''); }
    }

    return <dialog ref={dialogRef} className="send-confirm-dialog" aria-labelledby="send-confirm-title" onCancel={(event) => {
        event.preventDefault(); if (!busy) onClose();
    }}>
        <header><div><h3 id="send-confirm-title">{!config.simpleSend && (requiresAudienceStep || config.audienceOnly) ? '确认联系人名单' : '确认发送'}</h3>
            <p>{config.campaignName}</p></div><button type="button" aria-label="关闭发送确认" disabled={!!busy} onClick={onClose}>×</button></header>
        <div className="send-confirm-content">
            <div className="send-confirm-message"><span>{config.draftMode === 'ai' ? '邮件内容' : '邮件主题'}</span><strong>{config.draftMode === 'ai' ? '使用各联系人已审核的 AI 草稿' : config.subject}</strong></div>
            <p className="send-confirm-settings">本次最多 {config.limit.toLocaleString()} 位 · {config.senderProvider === 'outlook'
                ? `Outlook · ${config.senderAccountLimit} 个账号 · 每个账号每天最多 ${config.outlookPerSenderDailyLimit} 封`
                : 'SpaceMail'}</p>
            {busy === 'load' && <p role="status">正在检查名单、邮件和发送额度…</p>}
            {error && <div className="bulk-alert danger" role="alert">{error}</div>}
            {preflight && <PreflightSummary data={preflight} />}
            {approval && requiresAudienceStep && <p className="send-confirm-audience">将确认此活动中 {approval.approvableCount.toLocaleString()} 位待确认联系人。确认后显示本次实际可发送人数。</p>}
            {!config.simpleSend && approval && !audienceConfirmed && <div className="send-confirm-fields">
                <label className="bulk-field"><span>联系依据</span><select className="input" value={basis} disabled={!!busy || config.requiresConsent} onChange={(event) => setBasis(event.target.value as typeof basis)}>
                    <option value="consent">已取得并记录同意</option><option value="legitimate_interest_reviewed">已审核合法利益依据</option>
                </select></label>
                {basis === 'consent' && <label className="bulk-field"><span>同意记录</span><input className="input" value={evidence} disabled={!!busy} placeholder="填写 CRM 名单、表单或登记记录索引" onChange={(event) => setEvidence(event.target.value)} /></label>}
                <details open={config.sourceDescription.trim().length < 3 ? true : undefined}><summary>名单来源</summary><label className="bulk-field"><span>来源说明</span><textarea className="input" rows={2} value={source} disabled={!!busy} onChange={(event) => setSource(event.target.value)} /></label></details>
                <label className="bulk-check"><input type="checkbox" checked={reviewed} disabled={!!busy} onChange={(event) => setReviewed(event.target.checked)} /><span>我已核对联系人名单、联系依据，以及发件账号与邮件签名。</span></label>
            </div>}
            {audienceConfirmed && <p className="send-confirm-reviewed">名单已确认。{config.audienceOnly ? '现在可以继续生成草稿。' : '点击下方发送按钮后才会创建邮件任务。'}</p>}
        </div>
        <footer>
            <button type="button" className="btn btn-outline" disabled={!!busy} onClick={onClose}>{audienceConfirmed && !canSend ? '返回编辑' : '取消'}</button>
            {error && !busy && <button type="button" className="btn btn-outline" onClick={() => setRevision((value) => value + 1)}>重新检查</button>}
            {(approval || (config.simpleSend && preflight)) && <button type="button" className="btn btn-primary" disabled={!!busy || !reviewComplete || (!requiresAudienceStep && !canSend)} onClick={() => void (requiresAudienceStep ? confirmAudience() : send())}>
                {busy === 'review' ? '正在确认名单…' : busy === 'send' ? '正在创建任务…' : requiresAudienceStep ? '确认名单并继续' : `${config.simpleSend ? '开始发送' : '确认发送'} ${preflight?.eligibleCount.toLocaleString() || 0} 封`}
            </button>}
        </footer>
    </dialog>;
}
